import { redirect } from "@tanstack/react-router";

interface AuthGuardContext {
	auth: {
		isAuthenticated: boolean;
		user?: {
			onboardingCompleted: boolean;
			isOrgActive: boolean;
			isOrgFrozen: boolean;
		} | null;
	};
}

/**
 * Shared route guard for authenticated app surfaces: redirect to login when signed
 * out, to the suspended screen when the org is frozen, to onboarding when it isn't
 * finished, and to pricing when the org's subscription is merely inactive.
 *
 * THE ORDER IS THE CONTRACT. Each redirect answers "what is the FIRST thing standing
 * between this member and the product", and a member behind two of them must be sent
 * to the one they cannot get past — not the one they happen to be checked for first.
 *
 * The `_authenticated` layout applies this to everything under it; chrome-free
 * top-level routes that live OUTSIDE that layout (e.g. `/open-artifact`) call it
 * directly so they gate identically instead of drifting to a weaker check.
 */
export function requireActiveOnboardedUser({
	context,
	location,
}: {
	context: AuthGuardContext;
	location: { href: string };
}): void {
	if (!context.auth.isAuthenticated) {
		throw redirect({ to: "/auth/login", search: { redirect: location.href } });
	}
	// FROZEN IS CHECKED FIRST, before onboarding and before pricing.
	//
	// Before ONBOARDING because there is nothing to onboard into: every call that
	// flow makes — `PUT /api/auth/profile`, `POST /api/onboarding` — goes through
	// `authenticate`, which refuses a frozen org. Checking onboarding first walked
	// the member into a wizard where each step returned 403, which is how a contract
	// customer who had just set up their password met the product.
	//
	// That is not only the contract case: a member of a suspended org who never
	// finished signup was sent into the same dead flow.
	//
	// Never to /pricing, and before it: nothing the member can buy clears a freeze,
	// so a checkout page would be a dead end that looks like a billing problem they
	// caused. A frozen org is also usually partner-managed, which reports
	// `isOrgActive: true` — so without this arm most frozen members fall through the
	// guard entirely.
	if (context.auth.user?.isOrgFrozen) {
		throw redirect({ to: "/suspended" });
	}
	if (context.auth.user && !context.auth.user.onboardingCompleted) {
		throw redirect({ to: "/onboarding" });
	}
	if (context.auth.user && !context.auth.user.isOrgActive) {
		throw redirect({ to: "/pricing", search: { redirect: location.href } });
	}
}
